import { selectStandardProps } from "@/utils/componentUtils";
import { Form, Select } from "antd";
import { FormItemProps } from "antd/lib/form";
import { SelectProps } from "antd/lib/select";
import groupBy from "lodash/groupBy";
import { useTranslation } from "react-i18next";
import { DEFAULT_LOCALE } from "../../../../app/i18n";
import { useEnumeration } from "../../../../common/api/queries";
import type { UUID } from "../../../../typings/global";
import { LifeInsuranceTariffBase } from "../../../lifeinsurancetariff/types";
import { formatLifeInsuranceTariff } from "../../../lifeinsurancetariff/utils";
import { EnumerationType } from "../../enums";
import { FilterFunction } from "../../types";

interface Props {
  formItemProps: FormItemProps;
  selectProps?: SelectProps<string>;
  optionsProps?: TariffOptionsProps;
}

interface TariffOptionsProps {
  productId: UUID;
  groupByPriority?: boolean;
  filter?: FilterFunction<LifeInsuranceTariffBase>;
}

export const LifeInsuranceTariffSelect = ({ formItemProps, selectProps, optionsProps }: Props) => {
  const { t } = useTranslation();

  const productsEnums = useEnumeration(EnumerationType.PRODUCTS);
  const tariffGroupEnums = useEnumeration(EnumerationType.TARIFF_GROUPS);

  const resolveTariffSelectOptions = (): LifeInsuranceTariffBase[] => {
    let tariffs: LifeInsuranceTariffBase[] = [];

    if (optionsProps?.productId) {
      const product = productsEnums.find((product) => product.id === optionsProps.productId);

      if (product) {
        tariffs = tariffGroupEnums.find((group) => group.id === product.tariffGroupId)?.tariffs ?? [];
      }
    }

    if (optionsProps?.filter) {
      tariffs = tariffs.filter(optionsProps.filter);
    }

    return tariffs.sort((a, b) => a.name.localeCompare(b.name, DEFAULT_LOCALE, { sensitivity: "accent" }));
  };

  if (optionsProps?.groupByPriority) {
    const groups = groupBy(resolveTariffSelectOptions(), (tariff) => tariff.primaryTariff);
    const primaryTariffs = groups["true"];
    const secondaryTariffs = groups["false"];

    if (primaryTariffs?.length && secondaryTariffs?.length) {
      return (
        <Form.Item {...formItemProps}>
          <Select
            {...selectStandardProps}
            {...selectProps}
            options={[
              {
                label: t("lifeInsuranceTariff.helpers.primaryTariffs"),
                options:
                  primaryTariffs?.map((tariff) => ({
                    value: tariff.id,
                    label: formatLifeInsuranceTariff(tariff),
                  })) ?? [],
              },
              {
                label: t("lifeInsuranceTariff.helpers.secondaryTariffs"),
                options:
                  secondaryTariffs?.map((tariff) => ({
                    value: tariff.id,
                    label: formatLifeInsuranceTariff(tariff),
                  })) ?? [],
              },
            ]}
          />
        </Form.Item>
      );
    }
  }

  return (
    <Form.Item {...formItemProps}>
      <Select
        {...selectStandardProps}
        {...selectProps}
        options={resolveTariffSelectOptions().map((tariff) => ({
          value: tariff.id,
          label: formatLifeInsuranceTariff(tariff),
        }))}
      />
    </Form.Item>
  );
};
